/**
 * PRODUTO-V1 M2 — Recomendado pelo Nexo.
 *
 * Bloco abaixo do HeroFocusCard que mostra top 3 tarefas priorizadas
 * pelo briefing_ranker (server-side). Reusa o mesmo algoritmo do
 * briefing proativo — não há heurística nova no frontend.
 *
 * UX:
 *   - GET /api/tasks/recommended retorna [{task_id, title, reason, urgency, score}]
 *   - Cada item: "Atrasada — resolve primeiro" / "Vence hoje" + CTA "Começar" + CTA "Concluir"
 *   - Empty: oculta o bloco (não polui a tela com card vazio)
 *
 * Telemetria via executionTrace.js — só counts/flags, nunca títulos.
 */
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Play, Check, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import trace from "@/services/executionTrace";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URGENCY_CLS = {
  high: "text-red-400",
  medium: "text-amber-400",
  low: "text-slate-400",
};

export default function RecommendedBlock({ onStart, onComplete, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const fetchRecommended = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/tasks/recommended`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.tasks) ? data.tasks : [];
      setItems(list);
      try {
        trace.record("tasks.recommended.shown", {
          count: list.length,
          cached: !!data?.cached,
        });
      } catch {/* */}
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecommended(); }, [fetchRecommended, refreshKey]);

  const handleStart = useCallback(async (item) => {
    setActingId(item.task_id);
    try {
      trace.record("tasks.recommended.start", {
        urgency: item.urgency,
        position: items.findIndex(i => i.task_id === item.task_id),
      });
    } catch {/* */}
    try { await onStart?.({ id: item.task_id, title: item.title }); } catch {/* */}
    setActingId(null);
    fetchRecommended();
  }, [items, onStart, fetchRecommended]);

  const handleComplete = useCallback(async (item) => {
    setActingId(item.task_id);
    try {
      trace.record("tasks.recommended.complete", {
        urgency: item.urgency,
        position: items.findIndex(i => i.task_id === item.task_id),
      });
    } catch {/* */}
    try { await onComplete?.({ id: item.task_id, title: item.title }); } catch {/* */}
    setActingId(null);
    fetchRecommended();
  }, [items, onComplete, fetchRecommended]);

  if (loading) {
    return (
      <div
        className="glass-card rounded-2xl p-4 mb-4 border border-cyan-500/10 flex items-center gap-2 text-xs text-slate-500"
        data-testid="recommended-block-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Nexo está analisando…
      </div>
    );
  }

  if (!items.length) {
    // Empty: oculta o bloco. HeroFocusCard já cobre estado vazio geral.
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-2xl p-4 mb-4 border border-cyan-500/15"
      data-testid="recommended-block"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-white">Recomendado pelo Nexo</h3>
        <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">
          top {items.length}
        </span>
      </div>
      <ul className="space-y-2">
        <AnimatePresence>
          {items.map((item, idx) => {
            const urgencyCls = URGENCY_CLS[item.urgency] || URGENCY_CLS.medium;
            const isActing = actingId === item.task_id;
            return (
              <motion.li
                key={item.task_id}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]"
                data-testid={`recommended-item-${idx}`}
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-md bg-cyan-500/10 text-cyan-400 text-xs font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-200 truncate" title={item.title}>
                    {item.title}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${urgencyCls}`}>
                    {item.reason}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleStart(item)}
                    disabled={isActing}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/15 disabled:opacity-40 transition-colors"
                    data-testid={`recommended-start-${idx}`}
                  >
                    <Play className="w-3 h-3" />
                    Começar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleComplete(item)}
                    disabled={isActing}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-400 disabled:opacity-40 transition-colors"
                    aria-label="Marcar concluída"
                    data-testid={`recommended-complete-${idx}`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </motion.div>
  );
}
